import { Box, Chip, Stack, useTheme } from '@mui/joy';
import { X } from '@phosphor-icons/react';
import { FilterModel } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Button, SlidingPanel, Tabs } from 'core/components';
import { AgGridFilterOption, SortDirection } from 'core/enums';
import { IBreadcrumbsItem, ITab } from 'core/types';
import { useGetCorporatePart } from 'corporate/hooks';
import { PartNumberHelper } from 'parts/helpers';
import { useGetExternalPrices, useGetPart } from 'parts/hooks';
import { partListPath } from 'parts/routes';
import {
  IExternalPricesCustomization,
  IExternalPricesPayload,
  IExternalPricesResponse,
  IPart,
  IPartHistoryRequestPayload,
  IReusePriceConfig,
} from 'parts/types';
import { partToManufacturer, partToPartDetails, pickFirstExternalPricesBlock } from 'parts/utils';
import { Color, Direction, Size, Variant } from 'theme/enums';

import { COLLAPSE_TRANSITION_MS } from './constants';
import { Costs } from './Costs';
import { CustomizationRow } from './CustomizationRow';
import { PartDetailsTab } from './enums';
import { History } from './History';
import { PartDetailsFocusProvider, usePartDetailsFocusContext } from './hooks';
import { Inventory } from './Inventory';
import { Manufacturer } from './Manufacturer';
import { Media } from './Media';
import { PartCards } from './PartCards';
import { PartDetailsBreadcrumbs } from './PartDetailsBreadcrumbs';
import { Prices } from './Prices';
import { SalesHistory } from './SalesHistory';
import { SetUp } from './SetUp';
import { Specifications } from './Specifications';
import { Subtitle } from './Subtitle';
import { UoM } from './UoM';

export interface IPartDetailsPayload {
  accountsReceivableId?: string;
  salesExternalLocationId?: string;
}

interface IProps {
  part?: IPart | null;
  isOpen: boolean;
  onClose: () => void;
  onReusePrice?: (unitPrice: number) => void;
  defaultTab?: PartDetailsTab;
  partDetailsPayload?: IPartDetailsPayload;
  isAlternateVersionEnabled?: boolean;
  reusePriceDisabled?: boolean;
  reusePriceDisabledTooltip?: string;
  inline?: boolean;
  slidingPanelProps?: {
    inheritContentWidth?: boolean;
    interactive?: boolean;
    autoWidth?: boolean;
    level?: number;
    size?: Size;
  };
  historyConfig?: {
    requestPayloadFilters?: Partial<IPartHistoryRequestPayload['filters']>;
    initialFilter?: FilterModel;
  };
  salesHistoryConfig?: {
    partNumber?: string;
    initialFilter?: FilterModel;
  };
  subtitleAdditions?: {
    updatedAt?: string;
  };
  customizationDefaults?: Partial<IExternalPricesCustomization>;
}

const defaultSlidingPanelProps = {
  inheritContentWidth: false,
  interactive: false,
  autoWidth: true,
  level: 0,
};

export const PartDetails = (props: IProps) => (
  <PartDetailsFocusProvider>
    <PartDetailsContent {...props} />
  </PartDetailsFocusProvider>
);

const PartDetailsContent = ({
  part,
  isOpen,
  onClose,
  onReusePrice,
  defaultTab: customDefaultTab,
  partDetailsPayload,
  isAlternateVersionEnabled: initialIsAlternateVersionEnabled,
  reusePriceDisabled,
  reusePriceDisabledTooltip,
  inline = false,
  slidingPanelProps: customSlidingPanelProps,
  historyConfig,
  salesHistoryConfig,
  subtitleAdditions,
  customizationDefaults,
}: IProps) => {
  const { isHeaderCollapsed, setIsHeaderCollapsed, setActiveTab } = usePartDetailsFocusContext();

  const [partCardsResetKey, setPartCardsResetKey] = useState(0);
  const [appliedPricing, setAppliedPricing] = useState<IExternalPricesResponse | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();

  const { user } = useAuthUser();

  const applyIdRef = useRef(0);
  const previousPartKeyRef = useRef<string | number | undefined | null>(null);

  const isGuest = !user;

  const slidingPanelProps = useMemo(
    () => ({
      ...defaultSlidingPanelProps,
      ...customSlidingPanelProps,
    }),
    [customSlidingPanelProps]
  );

  const tabFullWidth = !inline && !slidingPanelProps?.size;

  const isAlternateVersionEnabled = useMemo(
    () =>
      initialIsAlternateVersionEnabled !== undefined
        ? initialIsAlternateVersionEnabled
        : Boolean(user?.alternateVersionEnabled),
    [initialIsAlternateVersionEnabled, user?.alternateVersionEnabled]
  );

  const mergedCustomizationDefaults: Partial<IExternalPricesCustomization> = useMemo(
    () => ({
      shippingLocation: customizationDefaults?.shippingLocation ?? user?.shippingLocation,
      salesLocation: customizationDefaults?.salesLocation ?? user?.location,
      shipmentType: customizationDefaults?.shipmentType,
      customerBillTo: customizationDefaults?.customerBillTo,
      project: customizationDefaults?.project,
    }),
    [
      customizationDefaults?.shippingLocation,
      customizationDefaults?.salesLocation,
      customizationDefaults?.shipmentType,
      customizationDefaults?.customerBillTo,
      customizationDefaults?.project,
      user?.shippingLocation,
      user?.location,
    ]
  );

  const corporatePartRequestPayload = useMemo(
    () => ({
      part: {
        number: part?.number,
        manufacturerId: part?.manufacturerId,
        familyId: part?.familyId,
        verified: part?.verified ? 1 : 0,
      },
      salesExternalLocationId: mergedCustomizationDefaults.salesLocation?.externalLocationId,
      shippingExternalLocationId: mergedCustomizationDefaults.shippingLocation?.externalLocationId,
      shipType: mergedCustomizationDefaults.shipmentType?.alternateCode,
      accountsReceivableId: mergedCustomizationDefaults.customerBillTo?.accountsReceivableId,
      projectId: mergedCustomizationDefaults.project?.id,
      ...partDetailsPayload,
      alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
    }),
    [
      part?.number,
      part?.manufacturerId,
      part?.familyId,
      part?.verified,
      mergedCustomizationDefaults,
      partDetailsPayload,
      isAlternateVersionEnabled,
    ]
  );

  const {
    record: corporatePartRecord,
    extraData: corporatePartExtraData,
    isLoading: isCorporatePartLoading,
  } = useGetCorporatePart(
    corporatePartRequestPayload,
    !isGuest && isOpen && !!part?.number && !!part?.manufacturerId
  );

  const corporatePart = useMemo(() => corporatePartRecord || {}, [corporatePartRecord]);

  const partRequestPayload = useMemo(
    () => ({ number: part?.number ?? '', manufacturer: part?.manufacturerUrlName ?? '' }),
    [part?.number, part?.manufacturerUrlName]
  );

  const {
    part: partRecord,
    manufacturer: manufacturerRecord,
    isLoading: isPartRecordLoading,
  } = useGetPart(partRequestPayload, isOpen && !!part?.number && !!part?.manufacturerUrlName);

  const { getExternalPrices, isLoading: isApplying } = useGetExternalPrices();

  const initialExternalPricing = useMemo<IExternalPricesResponse | null>(
    () =>
      pickFirstExternalPricesBlock<IExternalPricesResponse>(
        corporatePartExtraData?.externalPrices as
          | Record<string, IExternalPricesResponse>
          | undefined
      ),
    [corporatePartExtraData?.externalPrices]
  );

  const externalPricing = hasApplied ? appliedPricing : initialExternalPricing;

  const handleApply = useCallback(
    async (values: IExternalPricesCustomization): Promise<{ success: boolean }> => {
      if (!part?.number || !part?.manufacturerId) {
        return { success: false };
      }

      const myId = ++applyIdRef.current;

      const payload: IExternalPricesPayload = {
        part: { number: part.number, manufacturerId: part.manufacturerId },
        alternateVersionEnabled: 1,
        accountsReceivableId: values.customerBillTo?.accountsReceivableId,
        salesExternalLocationId: values.salesLocation?.externalLocationId,
        shippingExternalLocationId: values.shippingLocation?.externalLocationId,
        shipType: values.shipmentType?.alternateCode,
        projectId: values.project?.id,
      };

      const { success, record } = await getExternalPrices(payload);

      if (!success || myId !== applyIdRef.current) {
        return { success: false };
      }

      setAppliedPricing(record);
      setHasApplied(true);

      return { success: true };
    },
    [part?.number, part?.manufacturerId, getExternalPrices]
  );

  const userSelectedTabRef = useRef(false);

  const handleOnClose = useCallback(() => {
    applyIdRef.current += 1;
    setAppliedPricing(null);
    setHasApplied(false);
    setPartCardsResetKey((k) => k + 1);
    setIsHeaderCollapsed(false);
    userSelectedTabRef.current = false;
    onClose();
  }, [onClose, setAppliedPricing, setHasApplied, setIsHeaderCollapsed]);

  // BE returning null is treated the same as "still loading" — the IPart search row
  // is the source of truth for unverified/missing records (matches quote part_arr behavior).
  const subtitlePart = useMemo(
    () => partRecord ?? (part ? partToPartDetails(part) : null),
    [partRecord, part]
  );

  const manufacturer = useMemo(
    () => manufacturerRecord ?? (part ? partToManufacturer(part) : null),
    [manufacturerRecord, part]
  );

  const reusePrice = useMemo<IReusePriceConfig | undefined>(
    () =>
      onReusePrice
        ? {
            onReuse: onReusePrice,
            disabled: reusePriceDisabled,
            disabledTooltip: reusePriceDisabledTooltip,
          }
        : undefined,
    [onReusePrice, reusePriceDisabled, reusePriceDisabledTooltip]
  );

  const candidateDefaultTab = useMemo(() => {
    if (isGuest) {
      return customDefaultTab ?? PartDetailsTab.SPECIFICATIONS;
    }
    if (customDefaultTab) {
      return customDefaultTab;
    }
    if (isAlternateVersionEnabled) {
      return PartDetailsTab.SALES_HISTORY;
    }

    return PartDetailsTab.HISTORY;
  }, [isGuest, customDefaultTab, isAlternateVersionEnabled]);

  const tabs = useMemo<ITab[]>(() => {
    const quoteHistoryContent = (
      <History
        initialState={{
          filter: {
            filterModel: {
              ...historyConfig?.initialFilter,
              updatedAt: {
                filterType: 'date',
                type: AgGridFilterOption.RELATIVE_LAST_90_DAYS,
              },
            },
          },
          sort: {
            sortModel: [{ colId: 'updatedAt', sort: SortDirection.DESC }],
          },
        }}
        requestPayload={{
          filters: {
            partNumber: part?.number,
            manufacturerId: part?.manufacturerId,
            ...historyConfig?.requestPayloadFilters,
          },
        }}
        reusePrice={reusePrice}
        fullWidth={tabFullWidth}
      />
    );

    const salesHistoryContent = (
      <SalesHistory
        requestPayload={{
          filters: { partNumber: salesHistoryConfig?.partNumber ?? part?.vendorNumber },
        }}
        initialState={{
          filter: { filterModel: salesHistoryConfig?.initialFilter || {} },
        }}
        reusePrice={reusePrice}
        enabled={isOpen}
        fullWidth={tabFullWidth}
      />
    );

    const historyTab: ITab = isAlternateVersionEnabled
      ? {
          value: PartDetailsTab.HISTORY,
          label: t('labelHistory'),
          subTabs: [
            {
              value: PartDetailsTab.SALES_HISTORY,
              label: t('labelSalesHistory'),
              content: salesHistoryContent,
            },
            {
              value: PartDetailsTab.QUOTE_HISTORY,
              label: t('labelQuoteHistory'),
              content: quoteHistoryContent,
            },
          ],
          hide: isGuest,
        }
      : {
          value: PartDetailsTab.HISTORY,
          label: t('labelHistory'),
          content: quoteHistoryContent,
          hide: isGuest,
        };

    return [
      {
        value: PartDetailsTab.SPECIFICATIONS,
        label: t('labelSpecifications'),
        content: <Specifications part={part} enabled={isOpen} fullWidth={tabFullWidth} />,
      },
      historyTab,
      {
        value: PartDetailsTab.INVENTORY,
        label: t('labelInventory'),
        content: (
          <Inventory
            part={part}
            enabled={isOpen}
            isAlternateVersionEnabled={isAlternateVersionEnabled}
            fullWidth={tabFullWidth}
          />
        ),
        hide: isGuest,
      },
      {
        value: PartDetailsTab.COSTS,
        label: t('labelCosts'),
        content: (
          <Costs
            corporatePart={corporatePart}
            partNumber={part?.number}
            manufacturerId={part?.manufacturerId}
            externalPricing={externalPricing}
            isAlternateVersionEnabled={isAlternateVersionEnabled}
            enabled={isOpen}
            fullWidth={tabFullWidth}
            isLoading={isCorporatePartLoading}
          />
        ),
        // PNBs get a Calculated Fields tab instead — see #869d36jzc.
        hide: isGuest || !!corporatePart?.isPnb,
      },
      {
        value: PartDetailsTab.PRICES,
        label: t('labelPrices'),
        content: (
          <Prices
            corporatePart={corporatePart}
            partNumber={part?.number}
            manufacturerId={part?.manufacturerId}
            externalPricing={externalPricing}
            isAlternateVersionEnabled={isAlternateVersionEnabled}
            enabled={isOpen}
            fullWidth={tabFullWidth}
            isLoading={isCorporatePartLoading}
          />
        ),
        hide: isGuest || !!corporatePart?.isPnb,
      },
      {
        value: PartDetailsTab.UOM,
        label: t('labelUoms'),
        content: (
          <UoM
            enabled={isOpen}
            corporatePart={corporatePart}
            partNumber={part?.number}
            manufacturerId={part?.manufacturerId}
            fullWidth={tabFullWidth}
            isLoading={isCorporatePartLoading}
          />
        ),
        hide: isGuest,
      },
      {
        value: PartDetailsTab.SET_UP,
        label: t('labelPartDetails'),
        content: (
          <SetUp
            corporatePart={corporatePart}
            partNumber={part?.number}
            manufacturerId={part?.manufacturerId}
            fullWidth={tabFullWidth}
            isLoading={isCorporatePartLoading}
          />
        ),
        hide: isGuest,
      },
      {
        value: PartDetailsTab.MANUFACTURER,
        label: t('labelManufacturer'),
        content: (
          <Manufacturer
            manufacturer={manufacturer}
            fullWidth={tabFullWidth}
            isLoading={isPartRecordLoading}
          />
        ),
      },
      {
        value: PartDetailsTab.MEDIA,
        label: t('labelMedia'),
        content: <Media part={part} enabled={isOpen} fullWidth={tabFullWidth} />,
      },
    ];
  }, [
    t,
    historyConfig,
    salesHistoryConfig,
    part,
    manufacturer,
    isPartRecordLoading,
    tabFullWidth,
    reusePrice,
    isOpen,
    isAlternateVersionEnabled,
    isGuest,
    corporatePart,
    isCorporatePartLoading,
    externalPricing,
  ]);

  // Fall back to the first non-hidden tab when the candidate is hidden
  // (e.g. guest with `customDefaultTab` pointing at a logged-in-only tab).
  const resolvedDefaultTab = useMemo(() => {
    const isVisible = tabs.some((tab) => {
      if (tab.hide) {
        return false;
      }
      if (tab.value === candidateDefaultTab) {
        return true;
      }

      return tab.subTabs?.some((subTab) => subTab.value === candidateDefaultTab && !subTab.hide);
    });

    if (isVisible) {
      return candidateDefaultTab;
    }

    const fallback = tabs.find((tab) => !tab.hide)?.value as PartDetailsTab | undefined;

    return fallback ?? candidateDefaultTab;
  }, [tabs, candidateDefaultTab]);

  // Map subtab values (QUOTE_HISTORY/SALES_HISTORY) up to their parent (HISTORY)
  // since Tabs.onChange only fires for top-level tabs.
  const toTopLevelTab = useCallback((value: PartDetailsTab) => {
    if (value === PartDetailsTab.QUOTE_HISTORY || value === PartDetailsTab.SALES_HISTORY) {
      return PartDetailsTab.HISTORY;
    }

    return value;
  }, []);

  // Only sync activeTab from resolvedDefaultTab when the user hasn't clicked a
  // tab yet — otherwise async data (corporatePart loading, isPnb flipping
  // tabs hidden) would clobber the user's selection.
  useEffect(() => {
    if (!userSelectedTabRef.current) {
      setActiveTab(toTopLevelTab(resolvedDefaultTab as PartDetailsTab));
    }
  }, [resolvedDefaultTab, setActiveTab, toTopLevelTab]);

  const handleOnTabChange = useCallback(
    (value: string | number) => {
      userSelectedTabRef.current = true;
      setActiveTab(toTopLevelTab(value as PartDetailsTab));
      setIsHeaderCollapsed(false);
    },
    [setActiveTab, setIsHeaderCollapsed, toTopLevelTab]
  );

  useEffect(() => {
    const currentKey = part?.id ?? part?.number;

    if (previousPartKeyRef.current != null && previousPartKeyRef.current !== currentKey) {
      applyIdRef.current += 1;
      setAppliedPricing(null);
      setHasApplied(false);
      setPartCardsResetKey((k) => k + 1);
      setIsHeaderCollapsed(false);
      userSelectedTabRef.current = false;
      setActiveTab(toTopLevelTab(resolvedDefaultTab as PartDetailsTab));
    }

    previousPartKeyRef.current = currentKey;
  }, [
    part?.id,
    part?.number,
    setIsHeaderCollapsed,
    setActiveTab,
    toTopLevelTab,
    resolvedDefaultTab,
  ]);

  const titleNode = (
    <Trans
      i18nKey="titlePartDetails"
      values={{ partNumber: PartNumberHelper.toLabel(part?.number) }}
    />
  );

  const titleChip = subtitlePart?.isProprietary ? (
    <Chip
      color={Color.DANGER}
      variant={Variant.SOFT}
      sx={{
        fontWeight: 600,
        color: theme.palette.danger[800],
        backgroundColor: theme.palette.danger[200],
      }}
    >
      {t('labelProprietary')}
    </Chip>
  ) : null;

  const breadcrumbs = useMemo<IBreadcrumbsItem[]>(() => {
    const items: IBreadcrumbsItem[] = [{ text: t('labelAllParts'), to: partListPath() }];

    // Skip the synthetic root category at index 0 — BE always returns "All Parts" first.
    subtitlePart?.categories?.slice(1).forEach((category) => {
      if (category?.name) {
        items.push({ text: category.name });
      }
    });

    if (part?.number) {
      items.push({ text: PartNumberHelper.toLabel(part.number) });
    }

    return items;
  }, [subtitlePart?.categories, part?.number, t]);

  const breadcrumbsNode = <PartDetailsBreadcrumbs items={breadcrumbs} />;

  const onExpandPartDetails = useCallback(
    () => setIsHeaderCollapsed(false),
    [setIsHeaderCollapsed]
  );

  // `grid-template-rows: 1fr ↔ 0fr` animates auto-height without JS measurement;
  // negative marginTop cancels the parent Stack's gap when collapsed.
  const collapsibleSx = {
    display: 'grid',
    gridTemplateRows: isHeaderCollapsed ? '0fr' : '1fr',
    marginTop: isHeaderCollapsed ? '-16px !important' : '0px',
    opacity: isHeaderCollapsed ? 0 : 1,
    transition:
      `grid-template-rows ${COLLAPSE_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), ` +
      `margin-top ${COLLAPSE_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), ` +
      'opacity 180ms ease-out',
    '& > *': {
      minHeight: 0,
      overflow: 'hidden',
    },
  };

  const customizationRow =
    isOpen && isAlternateVersionEnabled && part?.number && part?.manufacturerId ? (
      <CustomizationRow
        partNumber={part.number}
        manufacturerId={part.manufacturerId}
        defaults={mergedCustomizationDefaults}
        onApply={handleApply}
        isApplying={isApplying}
      />
    ) : null;

  if (inline) {
    return (
      <Stack
        data-partdetails-wheel-surface="true"
        sx={{ gap: 2, height: '100%', minHeight: 0, overflow: 'hidden' }}
      >
        <Stack sx={{ gap: 0.5 }}>
          <Stack
            direction={Direction.ROW}
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            {breadcrumbsNode}

            <Button
              icon={<X />}
              variant={Variant.PLAIN}
              color={Color.NEUTRAL}
              onClick={handleOnClose}
            />
          </Stack>

          <Subtitle
            part={subtitlePart}
            manufacturer={manufacturer}
            updatedAt={subtitleAdditions?.updatedAt ?? corporatePart?.updatedAt}
            title={titleNode}
            chip={titleChip}
            isCollapsed={isHeaderCollapsed}
            onExpand={onExpandPartDetails}
          />
        </Stack>

        <Box sx={collapsibleSx}>
          <Stack sx={{ gap: 2 }}>
            {customizationRow}
            {isOpen && (
              <PartCards
                key={partCardsResetKey}
                corporatePart={corporatePart}
                externalPricing={externalPricing}
                isCorporatePartLoading={isCorporatePartLoading}
                isAlternateVersionEnabled={isAlternateVersionEnabled}
                inline
              />
            )}
          </Stack>
        </Box>

        {isOpen && (
          // overflowY: auto so useScrollToFocus's findScrollableAncestor finds
          // this box as the outer scroller in inline mode (no MuiDrawer).
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
            }}
          >
            <Tabs
              key={part?.id || part?.number}
              tabs={tabs}
              ariaLabel="partDetails"
              defaultTab={resolvedDefaultTab}
              hideTabBar={isHeaderCollapsed}
              onChange={handleOnTabChange}
            />
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <SlidingPanel
      key={part?.id || part?.number}
      isOpen={isOpen && (!!part?.id || !!part?.number)}
      onClosePanel={handleOnClose}
      title={breadcrumbsNode}
      subtitle={
        <Subtitle
          part={subtitlePart}
          manufacturer={manufacturer}
          updatedAt={subtitleAdditions?.updatedAt ?? corporatePart?.updatedAt}
          title={titleNode}
          chip={titleChip}
          isCollapsed={isHeaderCollapsed}
          onExpand={onExpandPartDetails}
        />
      }
      extra={
        isOpen && (
          <Box sx={collapsibleSx}>
            <Stack sx={{ gap: 2 }}>
              {customizationRow}
              <PartCards
                key={partCardsResetKey}
                corporatePart={corporatePart}
                externalPricing={externalPricing}
                isCorporatePartLoading={isCorporatePartLoading}
                isAlternateVersionEnabled={isAlternateVersionEnabled}
              />
            </Stack>
          </Box>
        )
      }
      {...slidingPanelProps}
    >
      {isOpen && (
        <Tabs
          key={part?.id || part?.number}
          tabs={tabs}
          ariaLabel="partDetails"
          defaultTab={resolvedDefaultTab}
          hideTabBar={isHeaderCollapsed}
          onChange={handleOnTabChange}
        />
      )}
    </SlidingPanel>
  );
};
